/**
 * Utilitaire centralisé de formatage de devise pour toute l'application.
 * La devise est celle de l'entité connectée (défaut : GNF).
 * Appeler `setCurrentDevise(devise)` au login pour configurer la devise.
 */

let _currentDevise = 'GNF';

/** Appelé par AuthContext/AppContext après login pour initialiser la devise de l'entité. */
export const setCurrentDevise = (devise: string) => {
  _currentDevise = devise || 'GNF';
};

/** Retourne la devise courante de l'entité. */
export const getCurrentDevise = (): string => _currentDevise;

/**
 * Formate un montant avec la devise de l'entité courante.
 * La devise est initialisée via setCurrentDevise() au login.
 * @example formatCurrency(1500000) => "1 500 000 GNF"
 * @example formatCurrency(0) => "0 GNF"
 * @example formatCurrency(null) => "0 GNF"
 */
export const formatCurrency = (value: number | string | null | undefined): string => {
  const numValue = Number(value) || 0;
  const formatted = new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(numValue);
  return `${formatted} ${_currentDevise}`;
};

/**
 * Variante PURE : formate avec une devise explicite, sans dépendre du state global.
 * À utiliser sur les pages publiques (accès sans login) où setCurrentDevise n'a
 * jamais été appelé et où on récupère la devise via le payload API.
 *
 * @param value - montant
 * @param devise - code ISO 4217 (XOF, XAF, EUR, USD, GNF…) — fallback 'GNF' si absent
 *
 * @example formatMontant(1500000, 'XOF') => "1 500 000 XOF"
 */
export const formatMontant = (
  value: number | string | null | undefined,
  devise: string | null | undefined,
): string => {
  const numValue = Number(value) || 0;
  const formatted = new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(numValue);
  return `${formatted} ${devise || 'GNF'}`;
};

/**
 * Formate un montant en version courte pour les axes de graphiques.
 * @example formatCurrencyShort(1500000) => "1.5M"
 * @example formatCurrencyShort(500000) => "500K"
 * @example formatCurrencyShort(800) => "800"
 */
export const formatCurrencyShort = (value: number): string => {
  if (Math.abs(value) >= 1_000_000_000) {
    return `${(value / 1_000_000_000).toFixed(1)}Md`;
  }
  if (Math.abs(value) >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1)}M`;
  }
  if (Math.abs(value) >= 1_000) {
    return `${(value / 1_000).toFixed(0)}K`;
  }
  return `${value}`;
};

/**
 * Formate un nombre sans symbole de devise.
 * @example formatNumber(1500000) => "1 500 000"
 */
export const formatNumber = (value: number | string | null | undefined): string => {
  const numValue = Number(value) || 0;
  return new Intl.NumberFormat('fr-FR').format(numValue);
};

/**
 * Formate un montant avec la devise FOURNIE, sans aucun repli : devise absente
 * → montant seul (jamais de devise supposée). Arrondi à l'unité ; les espaces
 * insécables (U+202F / U+00A0) de fr-FR sont remplacés par des espaces
 * ordinaires (reçus HTML imprimés / PDF).
 * @example formatMontantDevise(1500000, 'XOF') => "1 500 000 XOF"
 * @example formatMontantDevise(1500000, null)  => "1 500 000"
 */
export const formatMontantDevise = (
  value: number | string | null | undefined,
  devise: string | null | undefined,
): string => {
  const numValue = Number(value);
  const formatted = new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
    .format(Number.isFinite(numValue) ? numValue : 0)
    .replace(/[  ]/g, ' ');
  const code = typeof devise === 'string' ? devise.trim() : '';
  return code ? `${formatted} ${code}` : formatted;
};
